<template>
	<div
		ref="floatingRef"
		class="z-[9999]"
	>
		<div
			@click="setShowTooltip"
			class="cursor-pointer"
		>
            <slot name="menuButton">
                <EllipsisVerticalIcon
                    class="size-4 text-won-dark-gray"
                />
            </slot>
		</div>

		<Teleport to="#menus-container">
			<div
				ref="floating"
				v-show="showFloating"
				:style="{
                    ...floatingStyles,
                    ...positionStyles,
                    visibility: middlewareData?.hide?.referenceHidden
                        ? 'hidden'
                        : 'visible'
                }"
				class="z-[9999] shadow-float-select bg-white rounded overflow-hidden"
			>
                <div :class="menuOptionsWrapperClasses">
                    <div
                        v-for="option in menuOptions"
                        @click="() => emitHandler(option.emitEvent)"
                        :key="option.emitEvent"
                    >
                        <slot name="menuItem" :option="option">
                            <div
                                class="flex gap-1 items-center py-1.5 px-2.5 cursor-pointer hover:bg-won-bg-blue"
                            >
                                <component 
                                    :is="option.icon"
                                    :class="[menuIconsColor, 'size-4']"
                                />
                                <span class="text-[12px] leading-[15px] text-won-blue whitespace-nowrap">
                                    {{ option.actionText }}
                                </span>
                            </div>
                        </slot>
                    </div>
                </div>
			</div>
		</Teleport>
	</div>
</template>

<script setup>
import { ref, toRefs, Teleport, onMounted } from "vue";
import { storeToRefs } from "pinia";
import { useFloating, flip, offset, autoUpdate, hide } from "@floating-ui/vue";
import { onClickOutside } from "@vueuse/core";
import { useClickOutsideIgnore } from "../../composables/useClickOutsideIgnore";

import { useMousePosition } from "../../store/mouse-position.store";

import { EllipsisVerticalIcon } from "@heroicons/vue/24/outline";

const emit = defineEmits(['event']);
const props = defineProps({
	placement: {
		type: String,
		default: 'bottom'
	},
	menuIconsColor: {
		type: String,
		default: ''
	},
	menuOptionClasses: {
		type: String,
		default: ''
	},
	showOnMousePosition: {
		type: Boolean,
		default: false
	},
	disabled: {
		type: Boolean,
		default: false
	},
	menuOptions: {
		type: Array,
		default: []
	},
    menuRef: {
        type: Object,
        default: null
    },
    menuOptionsWrapperClasses: {
        type: String,
        defaultValue: ''
    }
})

const { placement, showOnMousePosition, disabled, menuRef } = toRefs(props)

const emitHandler = (emitEvent) => {
    emit('event', emitEvent)
}

const mousePositionStore = useMousePosition();
const { posX, posY } = storeToRefs(mousePositionStore);
const { addIgnoreEl } = useClickOutsideIgnore();

const floatingRef = ref(null)
const floating = ref(null);
const showFloating = ref(false);

onMounted(() => {
	addIgnoreEl(floating);
})

const { floatingStyles, middlewareData } = useFloating(floatingRef, floating, {
	placement: placement.value,
	middleware: [flip(), offset(4), hide()],
	whileElementsMounted: autoUpdate
});

const setShowTooltip = () => {
	if (disabled.value) return
	showFloating.value = true
}

const hideMenu = () => {
	if (disabled.value) return
	showFloating.value = false
}

onClickOutside(floatingRef, hideMenu);

const positionStyles = showOnMousePosition.value ? {top: `${posX}px`, left: `${posY}px`} : {}
</script>
